import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { 
  ArrowLeft, ArrowRight, Save, Send, X, 
  Heart, Brain, Sparkles, Users, Activity,
  CheckCircle2
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Slider } from '@/components/ui/slider';
import { Checkbox } from '@/components/ui/checkbox';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';

interface ClientIntakeFormProps {
  clinicianId?: string;
  onClose?: () => void;
  readOnly?: boolean;
  intakeId?: string;
}

const sections = [
  { id: 'physical', label: 'Physical', icon: Activity, colour: 'text-red-500' },
  { id: 'emotional', label: 'Emotional', icon: Heart, colour: 'text-pink-500' },
  { id: 'mental', label: 'Mental', icon: Brain, colour: 'text-blue-500' },
  { id: 'spiritual', label: 'Spiritual', icon: Sparkles, colour: 'text-purple-500' },
  { id: 'relational', label: 'Relational', icon: Users, colour: 'text-green-500' },
];

const emotionOptions = [
  'Joy', 'Peace', 'Love', 'Gratitude', 'Hope',
  'Anxiety', 'Sadness', 'Anger', 'Fear', 'Shame',
  'Guilt', 'Loneliness', 'Confusion', 'Overwhelm', 'Numbness'
];

const spiritualPractices = [
  'Meditation', 'Prayer', 'Yoga', 'Breathwork', 'Journaling',
  'Nature walks', 'Mindfulness', 'Energy healing', 'Gratitude practice',
  'Reading spiritual texts', 'Community worship', 'Silent reflection'
];

export const ClientIntakeForm: React.FC<ClientIntakeFormProps> = ({
  clinicianId,
  onClose,
  readOnly = false,
  intakeId
}) => {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [currentSection, setCurrentSection] = useState(0);
  const [formData, setFormData] = useState<any>({
    // Physical
    physical_health_rating: 5,
    sleep_quality: '',
    sleep_hours_average: 7,
    exercise_frequency: '',
    chronic_conditions: [],
    current_medications: '',
    physical_concerns: '',
    // Emotional
    emotional_state: '',
    primary_emotions: [],
    emotional_triggers: '',
    emotional_coping_strategies: '',
    emotional_support_network: '',
    emotional_concerns: '',
    // Mental
    mental_health_history: '',
    previous_therapy_experience: '',
    current_stressors: '',
    cognitive_patterns: '',
    mental_health_diagnoses: [],
    mental_goals: '',
    // Spiritual
    spiritual_beliefs: '',
    spiritual_practices: [],
    life_purpose_clarity: 5,
    meaning_sources: '',
    spiritual_concerns: '',
    // Relational
    relationship_status: '',
    family_dynamics: '',
    social_connections_quality: 5,
    communication_style: '',
    relationship_patterns: '',
    relational_concerns: '',
    // Overall
    primary_therapy_goals: '',
    expectations_from_therapy: '',
    preferred_session_frequency: 'weekly',
    emergency_contact_name: '',
    emergency_contact_phone: '',
    consent_to_share: false
  });

  // Fetch existing intake if editing
  const { data: existingIntake } = useQuery({
    queryKey: ['client-intake', intakeId],
    queryFn: async () => {
      if (!intakeId) return null;
      const { data, error } = await supabase
        .from('client_intake_forms')
        .select('*')
        .eq('id', intakeId)
        .single();
      if (error) throw error;
      return data;
    },
    enabled: !!intakeId
  });

  useEffect(() => {
    if (existingIntake) {
      setFormData(existingIntake);
    }
  }, [existingIntake]);

  const saveMutation = useMutation({
    mutationFn: async (status: 'draft' | 'submitted') => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      const payload = {
        ...formData,
        client_id: user.id,
        clinician_id: clinicianId || null,
        status,
        submitted_at: status === 'submitted' ? new Date().toISOString() : null
      };

      if (intakeId) {
        const { error } = await supabase
          .from('client_intake_forms')
          .update(payload)
          .eq('id', intakeId);
        if (error) throw error;
      } else {
        const { error } = await supabase
          .from('client_intake_forms')
          .insert(payload);
        if (error) throw error;
      }
    },
    onSuccess: (_, status) => {
      queryClient.invalidateQueries({ queryKey: ['client-intake'] });
      toast({
        title: status === 'submitted' ? 'Form submitted' : 'Progress saved',
        description: status === 'submitted' 
          ? 'Your intake form has been sent to your therapist.'
          : 'Your progress has been saved. You can continue later.'
      });
      if (status === 'submitted' && onClose) {
        onClose();
      }
    },
    onError: (error: any) => {
      toast({
        title: 'Error',
        description: error.message,
        variant: 'destructive'
      });
    }
  });

  const updateField = (field: string, value: any) => {
    setFormData((prev: any) => ({ ...prev, [field]: value }));
  };

  const toggleArrayItem = (field: string, item: string) => {
    setFormData((prev: any) => {
      const current = prev[field] || [];
      const updated = current.includes(item)
        ? current.filter((i: string) => i !== item)
        : [...current, item];
      return { ...prev, [field]: updated };
    });
  };

  const renderPhysicalSection = () => (
    <div className="space-y-6">
      <div className="space-y-3">
        <Label>Overall Physical Health Rating</Label>
        <div className="flex items-center gap-4">
          <Slider
            value={[formData.physical_health_rating]}
            onValueChange={([v]) => updateField('physical_health_rating', v)}
            max={10}
            min={1}
            step={1}
            className="flex-1"
            disabled={readOnly}
          />
          <span className="text-lg font-medium w-8">{formData.physical_health_rating}/10</span>
        </div>
      </div>

      <div className="grid sm:grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label>Sleep Quality</Label>
          <Select 
            value={formData.sleep_quality} 
            onValueChange={(v) => updateField('sleep_quality', v)}
            disabled={readOnly}
          >
            <SelectTrigger>
              <SelectValue placeholder="Select sleep quality" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="excellent">Excellent - Restorative</SelectItem>
              <SelectItem value="good">Good - Generally restful</SelectItem>
              <SelectItem value="fair">Fair - Some difficulties</SelectItem>
              <SelectItem value="poor">Poor - Frequent issues</SelectItem>
              <SelectItem value="very_poor">Very Poor - Chronic problems</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <div className="space-y-2">
          <Label>Average Sleep Hours</Label>
          <Input
            type="number"
            min={0}
            max={24}
            step={0.5}
            value={formData.sleep_hours_average}
            onChange={(e) => updateField('sleep_hours_average', parseFloat(e.target.value))}
            disabled={readOnly}
          />
        </div>
      </div>

      <div className="space-y-2">
        <Label>Exercise Frequency</Label>
        <Select 
          value={formData.exercise_frequency} 
          onValueChange={(v) => updateField('exercise_frequency', v)}
          disabled={readOnly}
        >
          <SelectTrigger>
            <SelectValue placeholder="How often do you exercise?" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="daily">Daily</SelectItem>
            <SelectItem value="several_weekly">Several times a week</SelectItem>
            <SelectItem value="weekly">Once a week</SelectItem>
            <SelectItem value="occasionally">Occasionally</SelectItem>
            <SelectItem value="rarely">Rarely</SelectItem>
            <SelectItem value="never">Never</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div className="space-y-2">
        <Label>Current Medications</Label>
        <Textarea
          placeholder="List any medications, supplements, or remedies you're currently taking..."
          value={formData.current_medications}
          onChange={(e) => updateField('current_medications', e.target.value)}
          rows={3}
          disabled={readOnly}
        />
      </div>

      <div className="space-y-2">
        <Label>Physical Health Concerns</Label>
        <Textarea
          placeholder="Any physical health concerns or conditions you'd like us to know about..."
          value={formData.physical_concerns}
          onChange={(e) => updateField('physical_concerns', e.target.value)}
          rows={3}
          disabled={readOnly}
        />
      </div>
    </div>
  );

  const renderEmotionalSection = () => (
    <div className="space-y-6">
      <div className="space-y-2">
        <Label>Current Emotional State</Label>
        <Textarea
          placeholder="Describe how you've been feeling emotionally recently..."
          value={formData.emotional_state}
          onChange={(e) => updateField('emotional_state', e.target.value)}
          rows={3}
          disabled={readOnly}
        />
      </div>

      <div className="space-y-3">
        <Label>Primary Emotions (select all that apply)</Label>
        <div className="flex flex-wrap gap-2">
          {emotionOptions.map((emotion) => (
            <Badge
              key={emotion}
              variant={formData.primary_emotions?.includes(emotion) ? 'default' : 'outline'}
              className={`cursor-pointer transition-all ${
                formData.primary_emotions?.includes(emotion) 
                  ? 'bg-primary' 
                  : 'hover:bg-primary/10'
              }`}
              onClick={() => !readOnly && toggleArrayItem('primary_emotions', emotion)}
            >
              {emotion}
            </Badge>
          ))}
        </div>
      </div>

      <div className="space-y-2">
        <Label>Emotional Triggers</Label>
        <Textarea
          placeholder="What situations, people, or events tend to trigger strong emotional responses?"
          value={formData.emotional_triggers}
          onChange={(e) => updateField('emotional_triggers', e.target.value)}
          rows={3}
          disabled={readOnly}
        />
      </div>

      <div className="space-y-2">
        <Label>Coping Strategies</Label>
        <Textarea
          placeholder="How do you currently cope with difficult emotions?"
          value={formData.emotional_coping_strategies}
          onChange={(e) => updateField('emotional_coping_strategies', e.target.value)}
          rows={3}
          disabled={readOnly}
        />
      </div>

      <div className="space-y-2">
        <Label>Emotional Support Network</Label>
        <Textarea
          placeholder="Who do you turn to for emotional support?"
          value={formData.emotional_support_network}
          onChange={(e) => updateField('emotional_support_network', e.target.value)}
          rows={2}
          disabled={readOnly}
        />
      </div>
    </div>
  );

  const renderMentalSection = () => (
    <div className="space-y-6">
      <div className="space-y-2">
        <Label>Mental Health History</Label>
        <Textarea
          placeholder="Brief overview of your mental health journey and any significant events..."
          value={formData.mental_health_history}
          onChange={(e) => updateField('mental_health_history', e.target.value)}
          rows={4}
          disabled={readOnly}
        />
      </div>

      <div className="space-y-2">
        <Label>Previous Therapy Experience</Label>
        <Textarea
          placeholder="Have you been in therapy before? What worked or didn't work for you?"
          value={formData.previous_therapy_experience}
          onChange={(e) => updateField('previous_therapy_experience', e.target.value)}
          rows={3}
          disabled={readOnly}
        />
      </div>

      <div className="space-y-2">
        <Label>Current Stressors</Label>
        <Textarea
          placeholder="What are the main sources of stress in your life right now?"
          value={formData.current_stressors}
          onChange={(e) => updateField('current_stressors', e.target.value)}
          rows={3}
          disabled={readOnly}
        />
      </div>

      <div className="space-y-2">
        <Label>Cognitive Patterns</Label>
        <Textarea
          placeholder="Are you aware of any recurring thought patterns or beliefs that affect you?"
          value={formData.cognitive_patterns}
          onChange={(e) => updateField('cognitive_patterns', e.target.value)}
          rows={3}
          disabled={readOnly}
        />
      </div>

      <div className="space-y-2">
        <Label>Mental Health Goals</Label>
        <Textarea
          placeholder="What would you like to achieve regarding your mental wellbeing?"
          value={formData.mental_goals}
          onChange={(e) => updateField('mental_goals', e.target.value)}
          rows={3}
          disabled={readOnly}
        />
      </div>
    </div>
  );

  const renderSpiritualSection = () => (
    <div className="space-y-6">
      <div className="space-y-2">
        <Label>Spiritual Beliefs (Optional)</Label>
        <Textarea
          placeholder="Describe any spiritual or philosophical beliefs that are important to you..."
          value={formData.spiritual_beliefs}
          onChange={(e) => updateField('spiritual_beliefs', e.target.value)}
          rows={3}
          disabled={readOnly}
        />
      </div>

      <div className="space-y-3">
        <Label>Spiritual Practices</Label>
        <div className="flex flex-wrap gap-2">
          {spiritualPractices.map((practice) => (
            <Badge
              key={practice}
              variant={formData.spiritual_practices?.includes(practice) ? 'default' : 'outline'}
              className={`cursor-pointer transition-all ${
                formData.spiritual_practices?.includes(practice) 
                  ? 'bg-accent' 
                  : 'hover:bg-accent/10'
              }`}
              onClick={() => !readOnly && toggleArrayItem('spiritual_practices', practice)}
            >
              {practice}
            </Badge>
          ))}
        </div>
      </div>

      <div className="space-y-3">
        <Label>Life Purpose Clarity</Label>
        <div className="flex items-center gap-4">
          <span className="text-sm text-muted-foreground">Unclear</span>
          <Slider
            value={[formData.life_purpose_clarity]}
            onValueChange={([v]) => updateField('life_purpose_clarity', v)}
            max={10}
            min={1}
            step={1}
            className="flex-1"
            disabled={readOnly}
          />
          <span className="text-sm text-muted-foreground">Very Clear</span>
        </div>
      </div>

      <div className="space-y-2">
        <Label>Sources of Meaning</Label>
        <Textarea
          placeholder="What gives your life meaning and purpose?"
          value={formData.meaning_sources}
          onChange={(e) => updateField('meaning_sources', e.target.value)}
          rows={3}
          disabled={readOnly}
        />
      </div>

      <div className="space-y-2">
        <Label>Spiritual Concerns</Label>
        <Textarea
          placeholder="Any spiritual or existential questions or concerns you're exploring?"
          value={formData.spiritual_concerns}
          onChange={(e) => updateField('spiritual_concerns', e.target.value)}
          rows={3}
          disabled={readOnly}
        />
      </div>
    </div>
  );

  const renderRelationalSection = () => (
    <div className="space-y-6">
      <div className="space-y-2">
        <Label>Relationship Status</Label>
        <Select 
          value={formData.relationship_status} 
          onValueChange={(v) => updateField('relationship_status', v)}
          disabled={readOnly}
        >
          <SelectTrigger>
            <SelectValue placeholder="Select your relationship status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="single">Single</SelectItem>
            <SelectItem value="dating">Dating</SelectItem>
            <SelectItem value="partnered">In a relationship</SelectItem>
            <SelectItem value="married">Married/Civil partnership</SelectItem>
            <SelectItem value="separated">Separated</SelectItem>
            <SelectItem value="divorced">Divorced</SelectItem>
            <SelectItem value="widowed">Widowed</SelectItem>
            <SelectItem value="other">Other/Prefer not to say</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div className="space-y-2">
        <Label>Family Dynamics</Label>
        <Textarea
          placeholder="Describe your family relationships and dynamics..."
          value={formData.family_dynamics}
          onChange={(e) => updateField('family_dynamics', e.target.value)}
          rows={3}
          disabled={readOnly}
        />
      </div>

      <div className="space-y-3">
        <Label>Quality of Social Connections</Label>
        <div className="flex items-center gap-4">
          <span className="text-sm text-muted-foreground">Isolated</span>
          <Slider
            value={[formData.social_connections_quality]}
            onValueChange={([v]) => updateField('social_connections_quality', v)}
            max={10}
            min={1}
            step={1}
            className="flex-1"
            disabled={readOnly}
          />
          <span className="text-sm text-muted-foreground">Well Connected</span>
        </div>
      </div>

      <div className="space-y-2">
        <Label>Communication Style</Label>
        <Select 
          value={formData.communication_style} 
          onValueChange={(v) => updateField('communication_style', v)}
          disabled={readOnly}
        >
          <SelectTrigger>
            <SelectValue placeholder="How would you describe your communication style?" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="passive">Passive - Tend to avoid conflict</SelectItem>
            <SelectItem value="aggressive">Aggressive - Direct and forceful</SelectItem>
            <SelectItem value="passive_aggressive">Passive-Aggressive</SelectItem>
            <SelectItem value="assertive">Assertive - Clear and respectful</SelectItem>
            <SelectItem value="varies">Varies by situation</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div className="space-y-2">
        <Label>Relationship Patterns</Label>
        <Textarea
          placeholder="Are there any recurring patterns you notice in your relationships?"
          value={formData.relationship_patterns}
          onChange={(e) => updateField('relationship_patterns', e.target.value)}
          rows={3}
          disabled={readOnly}
        />
      </div>

      <div className="space-y-2">
        <Label>Relational Concerns</Label>
        <Textarea
          placeholder="Any specific relationship concerns you'd like to address in therapy?"
          value={formData.relational_concerns}
          onChange={(e) => updateField('relational_concerns', e.target.value)}
          rows={3}
          disabled={readOnly}
        />
      </div>
    </div>
  );

  const renderFinalSection = () => (
    <div className="space-y-6">
      <div className="space-y-2">
        <Label>Primary Therapy Goals</Label>
        <Textarea
          placeholder="What are your main goals for therapy?"
          value={formData.primary_therapy_goals}
          onChange={(e) => updateField('primary_therapy_goals', e.target.value)}
          rows={4}
          disabled={readOnly}
        />
      </div>

      <div className="space-y-2">
        <Label>Expectations from Therapy</Label>
        <Textarea
          placeholder="What do you expect from the therapeutic process and your therapist?"
          value={formData.expectations_from_therapy}
          onChange={(e) => updateField('expectations_from_therapy', e.target.value)}
          rows={3}
          disabled={readOnly}
        />
      </div>

      <div className="space-y-2">
        <Label>Preferred Session Frequency</Label>
        <Select 
          value={formData.preferred_session_frequency} 
          onValueChange={(v) => updateField('preferred_session_frequency', v)}
          disabled={readOnly}
        >
          <SelectTrigger>
            <SelectValue placeholder="How often would you like to attend sessions?" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="twice_weekly">Twice weekly</SelectItem>
            <SelectItem value="weekly">Weekly</SelectItem>
            <SelectItem value="fortnightly">Fortnightly</SelectItem>
            <SelectItem value="monthly">Monthly</SelectItem>
            <SelectItem value="as_needed">As needed</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div className="grid sm:grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label>Emergency Contact Name</Label>
          <Input
            placeholder="Full name"
            value={formData.emergency_contact_name}
            onChange={(e) => updateField('emergency_contact_name', e.target.value)}
            disabled={readOnly}
          />
        </div>
        <div className="space-y-2">
          <Label>Emergency Contact Phone</Label>
          <Input
            placeholder="Phone number"
            value={formData.emergency_contact_phone}
            onChange={(e) => updateField('emergency_contact_phone', e.target.value)}
            disabled={readOnly}
          />
        </div>
      </div>

      {!readOnly && (
        <div className="flex items-start gap-3 p-4 bg-muted/30 rounded-lg border border-border/50">
          <Checkbox
            id="consent"
            checked={formData.consent_to_share}
            onCheckedChange={(checked) => updateField('consent_to_share', !!checked)}
          />
          <Label htmlFor="consent" className="text-sm leading-relaxed cursor-pointer">
            I consent to share this intake form with my therapist. I understand they will use this 
            information to better understand my needs and tailor my therapeutic journey. I can 
            update my sharing preferences at any time.
          </Label>
        </div>
      )}
    </div>
  );

  const renderSection = () => {
    switch (currentSection) {
      case 0: return renderPhysicalSection();
      case 1: return renderEmotionalSection();
      case 2: return renderMentalSection();
      case 3: return renderSpiritualSection();
      case 4: return renderRelationalSection();
      case 5: return renderFinalSection();
      default: return null;
    }
  };

  const currentSectionInfo = currentSection < 5 
    ? sections[currentSection] 
    : { id: 'final', label: 'Goals & Consent', icon: CheckCircle2, colour: 'text-secondary' };

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="border-b border-border/30 bg-card/50 backdrop-blur-sm sticky top-0 z-50">
        <div className="container mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            {onClose && (
              <Button variant="ghost" size="icon" onClick={onClose}>
                <X className="h-5 w-5" />
              </Button>
            )}
            <div>
              <h1 className="text-xl font-semibold text-foreground">
                Therappi® Intake Form
              </h1>
              <p className="text-sm text-muted-foreground">
                {readOnly ? 'Viewing submitted form' : 'Understanding the whole you'}
              </p>
            </div>
          </div>
          
          {!readOnly && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => saveMutation.mutate('draft')}
              disabled={saveMutation.isPending}
              className="gap-2"
            >
              <Save className="h-4 w-4" />
              Save Progress
            </Button>
          )}
        </div>
      </header>

      {/* Progress indicators */}
      <div className="container mx-auto px-4 py-4">
        <div className="flex gap-2 overflow-x-auto pb-2">
          {[...sections, { id: 'final', label: 'Goals', icon: CheckCircle2, colour: 'text-secondary' }].map((section, index) => (
            <button
              key={section.id}
              onClick={() => setCurrentSection(index)}
              className={`flex items-center gap-2 px-4 py-2 rounded-full whitespace-nowrap transition-all ${
                currentSection === index
                  ? 'bg-primary text-primary-foreground'
                  : 'bg-muted/50 hover:bg-muted text-muted-foreground'
              }`}
            >
              <section.icon className={`h-4 w-4 ${currentSection === index ? '' : section.colour}`} />
              <span className="text-sm font-medium">{section.label}</span>
            </button>
          ))}
        </div>
      </div>

      {/* Form content */}
      <main className="container mx-auto px-4 py-6 max-w-3xl">
        <motion.div
          key={currentSection}
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          exit={{ opacity: 0, x: -20 }}
          transition={{ duration: 0.2 }}
        >
          <Card className="bg-card/50 border-border/30">
            <CardHeader>
              <div className="flex items-center gap-3">
                <div className={`p-2 rounded-lg bg-muted/50 ${currentSectionInfo.colour}`}>
                  <currentSectionInfo.icon className="h-5 w-5" />
                </div>
                <div>
                  <CardTitle>{currentSectionInfo.label} Level</CardTitle>
                  <CardDescription>
                    {currentSection === 5 
                      ? 'Final details and consent'
                      : `Understanding your ${currentSectionInfo.label.toLowerCase()} wellbeing`}
                  </CardDescription>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              {renderSection()}
            </CardContent>
          </Card>
        </motion.div>

        {/* Navigation */}
        <div className="flex justify-between mt-6">
          <Button
            variant="outline"
            onClick={() => setCurrentSection(Math.max(0, currentSection - 1))}
            disabled={currentSection === 0}
            className="gap-2"
          >
            <ArrowLeft className="h-4 w-4" />
            Previous
          </Button>

          {currentSection < 5 ? (
            <Button
              onClick={() => setCurrentSection(currentSection + 1)}
              className="gap-2"
            >
              Next
              <ArrowRight className="h-4 w-4" />
            </Button>
          ) : !readOnly ? (
            <Button
              onClick={() => saveMutation.mutate('submitted')}
              disabled={saveMutation.isPending || !formData.consent_to_share}
              className="gap-2 bg-primary hover:bg-primary/90"
            >
              <Send className="h-4 w-4" />
              Submit Form
            </Button>
          ) : null}
        </div>
      </main>

      {/* Footer */}
      <footer className="border-t border-border/30 mt-12 py-6">
        <div className="container mx-auto px-4 text-center text-xs text-muted-foreground">
          <p>© 2026 Therappi® - Your information is encrypted and secure.</p>
        </div>
      </footer>
    </div>
  );
};

export default ClientIntakeForm;